import { useMemo, useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Download } from 'lucide-react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { meOptions } from '@/features/auth/api';
import { usePermission } from '@/features/auth/use-permission';
import { projectMembersOptions } from '@/features/projects/api';
import { leavesByProjectOptions } from '@/features/leaves/api';
import { holidaysOptions } from '@/features/holidays/api';
import { ExportLeavesDialog } from '@/features/leaves/components/ExportLeavesDialog';
import { LeavesTable, type LeaveRow } from '@/features/leaves/components/LeavesTable';
import {
  LeavesTimeline,
  type LeaveTimelineZoom,
} from '@/features/leaves/components/LeavesTimeline';
import { LogLeaveDialog } from '@/features/leaves/components/LogLeaveDialog';
import { todayIso } from '@/features/leaves/working-days';

const leavesSearchSchema = z.object({
  leaveZoom: z.enum(['day', 'week', 'month']).optional(),
});

export type LeavesSearch = z.infer<typeof leavesSearchSchema>;

export const Route = createFileRoute('/_authed/projects/$projectId/leaves')({
  validateSearch: leavesSearchSchema,
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(meOptions()),
      context.queryClient.ensureQueryData(projectMembersOptions(params.projectId)),
      context.queryClient.ensureQueryData(leavesByProjectOptions(params.projectId)),
      context.queryClient.ensureQueryData(holidaysOptions(params.projectId)),
    ]);
  },
  component: LeavesPage,
});

function LeavesPage() {
  const { projectId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const { data: me } = useSuspenseQuery(meOptions());
  const { data: members } = useSuspenseQuery(projectMembersOptions(projectId));
  const { data: allLeaves } = useSuspenseQuery(leavesByProjectOptions(projectId));
  const { data: holidays } = useSuspenseQuery(holidaysOptions(projectId));
  const canManageMembers = usePermission(projectId, 'ManageMembers');

  const leaveZoom: LeaveTimelineZoom = search.leaveZoom ?? 'week';
  const setZoom = (z: LeaveTimelineZoom) =>
    void navigate({
      search: (prev) => ({ ...prev, leaveZoom: z === 'week' ? undefined : z }),
      replace: true,
    });

  const { holidayDates, holidayNames } = useMemo(() => {
    const dates = new Set<string>();
    const names = new Map<string, string>();
    for (const h of holidays) {
      dates.add(h.date);
      if (h.name) names.set(h.date, h.name);
    }
    return { holidayDates: dates, holidayNames: names };
  }, [holidays]);

  const memberByUserId = useMemo(
    () => new Map(members.map((m) => [m.user_id, m])),
    [members],
  );

  const today = todayIso();

  const toRow = (leave: LeaveRow['leave']): LeaveRow => ({
    leave,
    ownerName: memberByUserId.get(leave.user_id)?.display_name ?? leave.user_id,
    ownerEmail: memberByUserId.get(leave.user_id)?.email ?? '',
    canDelete: leave.user_id === me.id || canManageMembers,
    canEdit: leave.user_id === me.id,
  });

  const tableRows = useMemo<LeaveRow[]>(
    () =>
      allLeaves
        .filter((l) => l.end_date >= today)
        .sort((a, b) => a.start_date.localeCompare(b.start_date))
        .map(toRow),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allLeaves, memberByUserId, me.id, canManageMembers, today],
  );

  const timelineRows = useMemo<LeaveRow[]>(
    () =>
      allLeaves
        .slice()
        .sort((a, b) => a.start_date.localeCompare(b.start_date))
        .map(toRow),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allLeaves, memberByUserId, me.id, canManageMembers],
  );

  const dialogMembers = canManageMembers
    ? members
    : members.filter((m) => m.user_id === me.id);

  const [logLeaveSeed, setLogLeaveSeed] = useState<{
    userId: string;
    start: string;
    end: string;
  } | null>(null);
  const [exportOpen, setExportOpen] = useState(false);

  return (
    <div className="flex flex-col gap-6 p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-base font-medium text-text">Leaves</h1>
          <p className="mt-0.5 text-xs text-text-muted">
            Upcoming and ongoing absences for this project.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => setExportOpen(true)}
            disabled={allLeaves.length === 0}
          >
            <Download className="h-3.5 w-3.5" aria-hidden="true" />
            Export CSV
          </Button>
          {dialogMembers.length > 0 ? (
            <Button
              type="button"
              size="sm"
              variant="primary"
              onClick={() => setLogLeaveSeed({ userId: me.id, start: '', end: '' })}
            >
              Log leave
            </Button>
          ) : null}
        </div>
      </div>

      <LeavesTable
        rows={tableRows}
        projectId={projectId}
        emptyTitle="No upcoming leaves on this project."
        emptyHint="Use Log leave to add one."
        summaryUserId={me.id}
      />

      <LeavesTimeline
        rows={timelineRows}
        zoom={leaveZoom}
        holidayDates={holidayDates}
        holidayNames={holidayNames}
        onZoomChange={setZoom}
      />

      <ExportLeavesDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        rows={timelineRows}
      />

      <LogLeaveDialog
        open={logLeaveSeed !== null}
        onClose={() => setLogLeaveSeed(null)}
        members={dialogMembers}
        meId={me.id}
        canFileForOthers={canManageMembers}
        projectId={projectId}
        defaultUserId={logLeaveSeed?.userId}
        defaultStart={logLeaveSeed?.start}
        defaultEnd={logLeaveSeed?.end}
      />
    </div>
  );
}
